// ABOUTME: React context for navigation state management
// ABOUTME: Provides cached nav data with event-driven invalidation

"use client"

import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from "react"
import { useAuth } from "@clerk/nextjs"

interface NavData {
  username: string | null
  unreadNotificationsCount: number
}

interface NavContextValue {
  navData: NavData
  loading: boolean
  // Refresh all nav data (username + notification count)
  refreshNavData: () => Promise<void>
  // Lightweight refresh for just notification count
  refreshNotificationCount: () => Promise<void>
  // Optimistically update notification count (e.g., after marking as read)
  decrementNotificationCount: (amount?: number) => void
  // Set notification count to specific value
  setNotificationCount: (count: number) => void
}

const defaultNavData: NavData = {
  username: null,
  unreadNotificationsCount: 0,
}

const NavContext = createContext<NavContextValue | null>(null)

export function NavProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, userId } = useAuth()
  const [navData, setNavData] = useState<NavData>(defaultNavData)
  const [loading, setLoading] = useState(true)
  const hasFetchedRef = useRef(false)
  const currentUserIdRef = useRef<string | null | undefined>(null)

  // Fetch full nav data (username + notification count)
  const refreshNavData = useCallback(async () => {
    if (!isSignedIn || !userId) {
      setNavData(defaultNavData)
      setLoading(false)
      return
    }

    try {
      const res = await fetch('/api/user/nav-data')
      if (res.ok) {
        const data = await res.json()
        setNavData({
          username: data.username,
          unreadNotificationsCount: data.unreadNotificationsCount ?? 0,
        })
      }
    } catch (error) {
      console.error('Failed to fetch nav data:', error)
    } finally {
      setLoading(false)
    }
  }, [isSignedIn, userId])

  // Lightweight fetch for just notification count
  const refreshNotificationCount = useCallback(async () => {
    if (!isSignedIn || !userId) return

    try {
      const res = await fetch('/api/user/notification-count')
      if (res.ok) {
        const data = await res.json()
        setNavData(prev => ({
          ...prev,
          unreadNotificationsCount: data.count ?? 0,
        }))
      }
    } catch (error) {
      console.error('Failed to fetch notification count:', error)
    }
  }, [isSignedIn, userId])

  // Optimistic update - decrement count (e.g., after reading notifications)
  const decrementNotificationCount = useCallback((amount = 1) => {
    setNavData(prev => ({
      ...prev,
      unreadNotificationsCount: Math.max(0, prev.unreadNotificationsCount - amount),
    }))
  }, [])

  // Set notification count to specific value
  const setNotificationCount = useCallback((count: number) => {
    setNavData(prev => ({
      ...prev,
      unreadNotificationsCount: count,
    }))
  }, [])

  // Initial fetch on mount or user change
  useEffect(() => {
    // Reset if user changed
    if (userId !== currentUserIdRef.current) {
      currentUserIdRef.current = userId
      hasFetchedRef.current = false
      setNavData(defaultNavData)
    }

    if (!hasFetchedRef.current && isSignedIn && userId) {
      hasFetchedRef.current = true
      refreshNavData()
    } else if (!isSignedIn) {
      setLoading(false)
      setNavData(defaultNavData)
    }
  }, [isSignedIn, userId, refreshNavData])

  return (
    <NavContext.Provider
      value={{
        navData,
        loading,
        refreshNavData,
        refreshNotificationCount,
        decrementNotificationCount,
        setNotificationCount,
      }}
    >
      {children}
    </NavContext.Provider>
  )
}

export function useNav() {
  const context = useContext(NavContext)
  if (!context) {
    throw new Error('useNav must be used within a NavProvider')
  }
  return context
}

// Hook for components that only need to read nav data (no refresh functions)
export function useNavData() {
  const { navData, loading } = useNav()
  return { ...navData, loading }
}
